// apps/web/src/features/commercial-proposals/components/CommercialProposalsListTable.tsx
//
// @aiPurpose desktop grid table for the commercial-proposals list (#1198).
//   Columns: code, client, product, status, [owner — Admin only], views,
//   updated. Rows are click/Enter/Space activatable and open the detail
//   drawer.
import { useTranslation } from 'react-i18next';
import { CommercialProposalStatus, type CommercialProposalListItem } from '@magimp/shared';

import {
  StatusBadge,
  WorkspaceTable,
  useInteractiveRow,
  type WorkspaceTableColumn,
} from '../../../ui/index.js';
import { formatDateTime } from '../../../i18n/index.js';
import type { AppLocale } from '../../../i18n/index.js';

// Owner column is appended only for an Admin (#1206), so the template gains a
// trailing 150px track in that mode.
const GRID_TEMPLATE = '110px minmax(160px,1fr) minmax(160px,1fr) 120px 90px 150px';
const GRID_TEMPLATE_WITH_OWNER = `${GRID_TEMPLATE} 150px`;

function statusBadgeStatus(status: CommercialProposalStatus): 'draft' | 'active' {
  return status === CommercialProposalStatus.PUBLISHED ? 'active' : 'draft';
}

export function CommercialProposalsListTable({
  rows,
  language,
  showOwner,
  onOpenRow,
}: {
  rows: readonly CommercialProposalListItem[];
  language: AppLocale;
  showOwner: boolean;
  onOpenRow: (id: string) => void;
}) {
  const { t } = useTranslation('commercialProposals');
  const columns: WorkspaceTableColumn[] = [
    { key: 'publicCode', label: t('columns.publicCode') },
    { key: 'client', label: t('columns.client') },
    { key: 'productName', label: t('columns.productName') },
    { key: 'status', label: t('columns.status') },
    { key: 'htmlViews', label: t('columns.htmlViews'), align: 'right' },
    { key: 'updatedAt', label: t('columns.updatedAt') },
  ];
  if (showOwner) {
    columns.push({ key: 'owner', label: t('columns.owner') });
  }
  return (
    <WorkspaceTable
      columnsTemplate={showOwner ? GRID_TEMPLATE_WITH_OWNER : GRID_TEMPLATE}
      columns={columns}
    >
      {rows.map((row) => (
        <ListRow
          key={row.id}
          row={row}
          language={language}
          showOwner={showOwner}
          onOpenRow={onOpenRow}
        />
      ))}
    </WorkspaceTable>
  );
}

function ListRow({
  row,
  language,
  showOwner,
  onOpenRow,
}: {
  row: CommercialProposalListItem;
  language: AppLocale;
  showOwner: boolean;
  onOpenRow: (id: string) => void;
}) {
  const { background, handlers } = useInteractiveRow({
    selected: false,
    onActivate: () => onOpenRow(row.id),
  });
  const cell = (firstCol: boolean, extra: React.CSSProperties = {}): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px',
    height: 44,
    fontSize: 13,
    color: 'var(--fg-1)',
    borderBottom: '1px solid var(--border-1)',
    background,
    cursor: 'pointer',
    minWidth: 0,
    transition: 'background var(--dur-1) var(--ease-out)',
    ...extra,
  });
  return (
    <>
      <div
        {...handlers}
        data-testid={`commercial-proposals-row-${row.id}`}
        style={cell(true, { fontVariantNumeric: 'tabular-nums' })}
      >
        {row.publicCode}
      </div>
      <div {...handlers} style={cell(false, { overflow: 'hidden', textOverflow: 'ellipsis' })}>
        {row.client}
      </div>
      <div {...handlers} style={cell(false, { overflow: 'hidden', textOverflow: 'ellipsis' })}>
        {row.productName}
      </div>
      <div {...handlers} style={cell(false)}>
        <StatusBadge status={statusBadgeStatus(row.status)} size="sm" />
      </div>
      <div
        {...handlers}
        data-testid={`commercial-proposals-views-${row.id}`}
        style={cell(false, { justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' })}
      >
        {row.htmlViewCount}
      </div>
      <div
        {...handlers}
        style={cell(false, { color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' })}
      >
        {formatDateTime(row.updatedAt, language)}
      </div>
      {showOwner && (
        <div
          {...handlers}
          data-testid={`commercial-proposals-owner-${row.id}`}
          style={cell(false, { overflow: 'hidden', textOverflow: 'ellipsis' })}
        >
          {row.ownerName}
        </div>
      )}
    </>
  );
}
